'use client';

import { Suspense, useEffect, type ReactNode } from 'react';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { initAnalytics } from './lib/posthog-client';
import { PageViewTracker } from './page-view-tracker';
import { AnalyticsIdentify } from './analytics-identify';

/**
 * Client-side analytics root.
 *
 * Mounts once under the session provider. `initAnalytics()` runs in the render
 * body (client-guarded, so SDK setup never happens during SSR), with the effect
 * call kept as a fallback; it is idempotent and no-ops when the public key is
 * missing, leaving every downstream call a safe no-op.
 *
 * `PostHogProvider` receives the already-imported `posthog` singleton via
 * `client` (the `posthog-js@1.428.x` API) rather than the `apiKey`/`options`
 * form — configuration stays solely in `initAnalytics()`.
 *
 * The headless trackers use `useSearchParams()`, so App Router requires them to
 * sit inside a `<Suspense>` boundary.
 */
export function AnalyticsProvider({ children }: { children: ReactNode }) {
  // Init in the render body (not only in the effect): React flushes child
  // passive effects before the parent's, so an effect-only init would leave
  // `PageViewTracker`'s first effect seeing an uninitialised client and drop the
  // landing `$pageview`. `initAnalytics()` is idempotent and client-guarded, so
  // calling it during render — including a StrictMode double-invoke — is safe.
  if (typeof window !== 'undefined') {
    initAnalytics();
  }

  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <PostHogProvider client={posthog}>
      <Suspense fallback={null}>
        <PageViewTracker />
        <AnalyticsIdentify />
      </Suspense>
      {children}
    </PostHogProvider>
  );
}
